import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from './api/client';
import { Layout } from './components/Layout';
import { ToastProvider } from './components/Toast';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import {
  ConfirmEmailPage,
  ForgotPasswordPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
} from './pages/AuthPages';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import {
  BlogDetailPage,
  BlogListPage,
  BrandListPage,
  BrandPage,
  CategoryPage,
  CollectionListPage,
  CollectionPage,
  ContactPage,
  FaqPage,
  NewsletterActionPage,
  NotFoundPage,
  OrderTrackingPage,
  StaticPage,
} from './pages/ContentPages';
import { HomePage } from './pages/HomePage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { ProductListPage } from './pages/ProductListPage';
import { AccountLayout } from './pages/account/AccountLayout';
import { MyOrderDetailPage, MyOrdersPage, MyReturnsPage } from './pages/account/OrdersPages';
import {
  AccountOverviewPage,
  AddressesPage,
  OrderSuccessPage,
  ProfilePage,
  SecurityPage,
  WishlistPage,
} from './pages/account/ProfilePages';
import { CouponsPage } from './pages/account/CouponsPage';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Yetki ve bulunamadi hatalarinda tekrar denemek anlamsizdir.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
    },
    mutations: { retry: false },
  },
});

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <HomePage /> },

      { path: 'urunler', element: <ProductListPage /> },
      { path: 'urun/:slug', element: <ProductDetailPage /> },
      { path: 'kategori/:slug', element: <CategoryPage /> },
      { path: 'markalar', element: <BrandListPage /> },
      { path: 'marka/:slug', element: <BrandPage /> },
      { path: 'koleksiyonlar', element: <CollectionListPage /> },
      { path: 'koleksiyon/:slug', element: <CollectionPage /> },

      { path: 'sepet', element: <CartPage /> },
      { path: 'odeme', element: <CheckoutPage /> },
      { path: 'siparis-tamam/:publicId', element: <OrderSuccessPage /> },
      { path: 'siparis-takip', element: <OrderTrackingPage /> },
      { path: 'siparis-takip/:publicId', element: <OrderTrackingPage /> },

      { path: 'giris', element: <LoginPage /> },
      { path: 'kayit', element: <RegisterPage /> },
      { path: 'parola-sifirla', element: <ForgotPasswordPage /> },
      { path: 'parola-yenile', element: <ResetPasswordPage /> },
      { path: 'eposta-dogrula', element: <ConfirmEmailPage /> },

      {
        path: 'hesabim',
        element: <AccountLayout />,
        children: [
          { index: true, element: <AccountOverviewPage /> },
          { path: 'kuponlarim', element: <CouponsPage /> },
          { path: 'siparisler', element: <MyOrdersPage /> },
          { path: 'siparisler/:publicId', element: <MyOrderDetailPage /> },
          { path: 'iadeler', element: <MyReturnsPage /> },
          { path: 'favoriler', element: <WishlistPage /> },
          { path: 'adresler', element: <AddressesPage /> },
          { path: 'profil', element: <ProfilePage /> },
          { path: 'guvenlik', element: <SecurityPage /> },
        ],
      },

      { path: 'sayfa/:slug', element: <StaticPage /> },
      { path: 'blog', element: <BlogListPage /> },
      { path: 'blog/:slug', element: <BlogDetailPage /> },
      { path: 'sss', element: <FaqPage /> },
      { path: 'iletisim', element: <ContactPage /> },
      { path: 'bulten', element: <NewsletterActionPage /> },
      { path: 'bulten-onay', element: <NewsletterActionPage /> },
      { path: 'bulten-iptal', element: <NewsletterActionPage /> },
      { path: 'bulten-cikis', element: <NewsletterActionPage /> },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <CartProvider>
              <RouterProvider router={router} />
            </CartProvider>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>,
);
